import React, { useContext, useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import moment from "moment";
import { getUserId } from "@crema/utility/AppHooks";

export const GET_TASK_COUNT_SUMMARY = gql`
  query GetTaskCountSummary($id: id!, $todayDate: String!, $userType: String!) {
    GetTaskCountSummary(id: $id, todayDate: $todayDate, userType: $userType) {
      tab_counts
      message
      status_code
    }
  }
`;


const POLL_INTERVAL_MS = 120000;

export interface TaskCounts {
  assigned_all: number;
  assigned_duetoday: number;
  assigned_overdue: number;
  assigned_incomplete: number;
  assigned_urgent: number;
  assigned_completed: number;
  assigned_unassigned: number;
  created_all: number;
  created_duetoday: number;
  created_overdue: number;
  created_incomplete: number;
  created_urgent: number;
  created_completed: number;
  created_unassigned: number;
  all_all: number;
  all_duetoday: number;
  all_overdue: number;
  all_incomplete: number;
  all_urgent: number;
  all_completed: number;
  all_unassigned: number;
}

export const EMPTY_TASK_COUNTS: TaskCounts = {
  assigned_all: 0,
  assigned_duetoday: 0,
  assigned_overdue: 0,
  assigned_incomplete: 0,
  assigned_urgent: 0,
  assigned_completed: 0,
  assigned_unassigned: 0,
  created_all: 0,
  created_duetoday: 0,
  created_overdue: 0,
  created_incomplete: 0,
  created_urgent: 0,
  created_completed: 0,
  created_unassigned: 0,
  all_all: 0,
  all_duetoday: 0,
  all_overdue: 0,
  all_incomplete: 0,
  all_urgent: 0,
  all_completed: 0,
  all_unassigned: 0,
};

export interface TaskCountContextValue {
  counts: TaskCounts;
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

const defaultContextValue: TaskCountContextValue = {
  counts: EMPTY_TASK_COUNTS,
  loading: false,
  error: false,
  refetch: () => {},
};

const TaskCountContext = React.createContext<TaskCountContextValue>(
  defaultContextValue
);


export const useTaskCountContext = () => useContext(TaskCountContext);

const parseCounts = (
  data: { GetTaskCountSummary?: { status_code?: number; tab_counts?: string } } | undefined
): TaskCounts => {
  if (data?.GetTaskCountSummary?.status_code !== 200) {
    return EMPTY_TASK_COUNTS;
  }
  try {
    const parsed = JSON.parse(data.GetTaskCountSummary.tab_counts || "{}");
    return { ...EMPTY_TASK_COUNTS, ...parsed };
  } catch (e) {
    return EMPTY_TASK_COUNTS;
  }
};

const TaskCountProvider: React.FC = ({ children }) => {
  const currentUserId = getUserId();
  const todayDate = moment().format("DD/MM/YYYY");

  const { data, loading, error, refetch } = useQuery(GET_TASK_COUNT_SUMMARY, {
    variables: {
      id: currentUserId,
      todayDate,
      userType: "patient",
    },
    skip: !currentUserId,
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: "network-only",
  });

  const counts = useMemo(() => parseCounts(data), [data]);

  const value = useMemo<TaskCountContextValue>(
    () => ({
      counts,
      loading,
      error: Boolean(error),
      refetch: () => {
        refetch().catch(() => {});
      },
    }),
    [counts, loading, error, refetch]
  );

  return (
    <TaskCountContext.Provider value={value}>
      {children}
    </TaskCountContext.Provider>
  );
};

export default TaskCountProvider;
